import d from "./debug";

const SuperSimpleStateSystem = <State extends Object>(init: State) => {
  let state: State = init;
  let subs: { [k in keyof State]?: ((old: State) => void)[] } = {};
  let subIdx = 0;
  const subOrder = new WeakMap<(old: State) => void, number>();

  const sub = <K extends keyof State>(
    keys: K | K[],
    callback: (old: State) => void,
  ) => {
    subOrder.set(callback, subIdx++);
    const keySet = new Set(Array.isArray(keys) ? keys : [keys]);
    for (let key of keySet) {
      if (!state.hasOwnProperty(key)) {
        throw new Error("Can't subscribe to a key not found in State");
      }
      if (!subs[key]) {
        subs[key] = [];
      }

      subs[key]!.push(callback);
    }

    return () => {
      for (let key of keySet) {
        subs[key] = subs[key]!.filter((cb) => cb !== callback);
      }
    };
  };

  const waitFor = async <K extends keyof State>(
    key: K,
    condition: (initial: State[K], cur: State[K]) => boolean,
  ) => {
    return new Promise<State[K]>((resolve) => {
      const initial = get(key);
      const unsub = sub(key, () => {
        const thing = get(key);
        if (condition(initial, thing)) {
          unsub();
          resolve(thing);
        }
      });
    });
  };

  const mod = <K extends keyof State>(key: K, val: State[K]) => {
    if (!state.hasOwnProperty(key)) {
      throw new Error("EEEEE");
    }
    const old = { ...state };
    state[key] = val;
    for (let cb of subs[key] || []) {
      d(4, "running callback", { key, cb });
      cb(old);
    }
  };

  const isKey = (
    maybeKey: number | string | symbol,
  ): maybeKey is keyof State => {
    return state.hasOwnProperty(maybeKey);
  };

  // updateViaProxy: Allows multiple state changes to be made simultaneously, so
  // that observers of more than one of these changes aren't run multiple times.

  // For example, take sub(['cat', 'dog'], () => bark()).  If cat and dog change
  // simultaneously, we expect one bark.

  // set('cat', 'meowtwo'); set('dog', 'fido'); runs everything subbed to cat,
  // THEN everything subbed to dog. Since the sub is for both, bark would happen
  // twice here.

  // updateViaProxy((st) => {st.cat = 'meowtwo'; st.dog = 'fido'}) changes both
  // state values in one go, and anything subbed to cat and dog will only fire
  // once.

  // NOTE: This doesn't support deep edits. Only the top level values associated
  // with state keys can be edited. This is consistent with how s4 works, in
  // that you can only subscribe to changes on the top level of state keys. The
  // idea behind this is to keep things easy to reason about - if you want more
  // granular subscriptions, split the value into multiple top-level keys.
  const updateViaProxy = (doEdit: (s: State) => void) => {
    const old = { ...state };

    const propsSeen = new Set<keyof State>();
    const proxy = new Proxy(state, {
      set(obj, prop, newVal, receiver) {
        if (!isKey(prop)) {
          throw new Error("can't set prop that doesn't exist in state.");
        }
        propsSeen.add(prop);
        Reflect.set(obj, prop, newVal, receiver);
        return true;
      },
    });

    doEdit(proxy);

    const callbacksSeen = new Set<(old: State) => void>();
    for (let prop of propsSeen) {
      for (let cb of subs[prop] || []) {
        if (!callbacksSeen.has(cb)) {
          callbacksSeen.add(cb);
        }
      }
    }

    for (let cb of Array.from(callbacksSeen).sort(
      (a, b) => subOrder.get(a)! - subOrder.get(b)!,
    )) {
      cb(old);
    }
  };

  function update(updates: Partial<State>, force: boolean = false) {
    const old = { ...state };
    const propsSeen = new Set<keyof State>();
    for (let _k of Object.keys(updates)) {
      const k = _k as keyof State;

      if (!state.hasOwnProperty(k)) {
        throw new Error("EEEEE");
      }

      if (updates[k] !== state[k] || force) {
        propsSeen.add(k);
        state[k] = updates[k]!;
      }
    }

    const callbacksSeen = new Set<(old: State) => void>();
    for (let prop of propsSeen) {
      for (let cb of subs[prop] || []) {
        if (!callbacksSeen.has(cb)) {
          callbacksSeen.add(cb);
        }
      }
    }

    for (let cb of Array.from(callbacksSeen).sort(
      (a, b) => subOrder.get(a)! - subOrder.get(b)!,
    )) {
      cb(old);
    }
  }

  interface Getter {
    (): State;
    <K extends keyof State>(key: K): State[K];
  }

  const get: Getter = <K extends keyof State>(key?: K) =>
    key ? state[key] : state;

  return {
    get,
    mod,
    sub,
    waitFor,
    updateViaProxy,
    update,
  };
};

type SSSSInitializer<S extends Object> = typeof SuperSimpleStateSystem<S>;
type SSSS<S extends Object> = ReturnType<SSSSInitializer<S>>;

export default SuperSimpleStateSystem;
export type { SSSSInitializer, SSSS };
